import {
	type Store,
	type LineItem,
	type ProductVariant,
} from '@commercetools/platform-sdk/dist/commercetools-platform-sdk.cjs'
import {
	type DataFunctionArgs,
	json,
	type LoaderFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { ShippingKeys } from '#app/constants/shipping-keys.ts'
import OutfitPdp from '#app/features/shop-the-look/outfit-pdp.tsx'

import { addLineItems } from '#app/server/commerce-tools/cart/addLineItems.server.ts'
import { getCart } from '#app/server/commerce-tools/cart/getCart.server.ts'
import { sortOutfitVariantBySize } from '#app/server/commerce-tools/product/utils.server.ts'
import {
	getStoreByKey,
	getStores,
} from '#app/server/commerce-tools/store/store.server.ts'
import { getOutfitByKey, getOutfits } from '#app/server/partner.server.ts'
import { type Outfit } from '#types/outfit.ts'
interface OutFitProduct {
	variant: ProductVariant
	storeKey?: string
	productId: string
	shippingMethod: string
}

interface OutfitBagItem {
	productId: string
	variantId: number
	channelId: string
	quantity: number
	shippingMethod: string
}

export const loader: LoaderFunction = async ({
	request,
	params,
}: LoaderFunctionArgs) => {
	const { slug } = params
	if (!slug) return null

	const stores = await getStores()

	const storeChannels = stores.map(store => {
		return { ...store.distributionChannels[0].obj, storeKey: store.key }
	})

	const outfit: Outfit = await getOutfitByKey(slug)

	outfit.styleVariants.forEach(style => {
		style.variants = style.variants.sort(sortOutfitVariantBySize)
	})

	const UnfilteredRelatedOutfits = await getOutfits(
		request,
		undefined,
		outfit.storeKey,
		outfit.gender,
		'newest',
	)
	const relatedOutfits = UnfilteredRelatedOutfits.filter(
		relatedOutfit => relatedOutfit.key !== outfit.key,
	)

	return { outfit, relatedOutfits, storeChannels }
}

const OutfitDetailsPage = () => {
	return (
		<div>
			<OutfitPdp />
		</div>
	)
}

export const action = async ({ request }: DataFunctionArgs) => {
	const oldCart = await getCart(request)
	if (!oldCart)
		return json({ status: 'error', newItems: [], newDistributionChannel: null })

	const formData = await request.formData()

	const products = JSON.parse(
		formData.get('products') as string,
	) as OutFitProduct[]

	const addToBagItems: OutfitBagItem[] = await Promise.all(
		products.map(async product => {
			let selectedChannelId: string | null = null

			if (product.storeKey) {
				const selectedStore = await getStoreByKey(product.storeKey)

				if (selectedStore) {
					const isAvailableInStore = isProductAvailableInStore(
						product.variant,
						selectedStore,
					)

					if (isAvailableInStore) {
						selectedChannelId = selectedStore.distributionChannels[0].id
					}
				}
			}

			if (!selectedChannelId) {
				const lineItemChannelId =
					findChannelWithHighestStock(
						product.variant.availability?.channels as any,
						oldCart.lineItems,
					) || ''
				selectedChannelId = lineItemChannelId
			}

			let shippingMethod: ShippingKeys

			switch (product.shippingMethod) {
				case 'pickup':
					shippingMethod = ShippingKeys.Pickup
					break
				case 'delivery':
				default:
					shippingMethod = ShippingKeys.Delivery
					break
			}
			return {
				productId: product.productId,
				variantId: product.variant.id,
				channelId: selectedChannelId,
				quantity: 1,
				shippingMethod: shippingMethod,
			}
		}),
	)

	const response = await addLineItems(request, addToBagItems)

	const newItems = response?.body.lineItems.filter(item => {
		return !oldCart.lineItems.find(lineItem => {
			return lineItem.id === item.id && lineItem.quantity === item.quantity
		})
	})

	return json({
		status: 'success',
		newItems,
		newDistributionChannel: undefined,
	})
}

const findChannelWithHighestStock = (
	channels: {
		[key: string]: { availableQuantity: number; id: string }
	},
	lineItems: LineItem[],
) => {
	let highestStockChannelId: string | null = null
	let highestStock = 0
	let isSameStock = true
	let firstChannel = true
	let firstStock = 0

	const allExistingChannelIdsInCart = lineItems
		.map(item => item.distributionChannel?.id)
		.filter((id): id is string => id !== undefined)

	for (const key in channels) {
		const currentStock = channels[key].availableQuantity
		if (firstChannel) {
			firstStock = currentStock
			firstChannel = false
		} else if (currentStock !== firstStock) {
			isSameStock = false
		}

		if (currentStock > highestStock) {
			highestStock = currentStock
			highestStockChannelId = key
		}
	}

	for (const pickedChannelId of allExistingChannelIdsInCart) {
		if (
			pickedChannelId &&
			channels[pickedChannelId] &&
			channels[pickedChannelId].availableQuantity > 1 &&
			channels[pickedChannelId].availableQuantity >= highestStock - 2
		) {
			return pickedChannelId
		}
	}

	if (isSameStock) {
		for (const pickedChannelId of allExistingChannelIdsInCart) {
			if (pickedChannelId && channels[pickedChannelId]) {
				return pickedChannelId
			}
		}

		const channelKeys = Object.keys(channels)
		return channelKeys[Math.floor(Math.random() * channelKeys.length)]
	}
	return highestStockChannelId
}

const isProductAvailableInStore = (
	variant: ProductVariant,
	store: Store,
): boolean => {
	const storeChannelId = store.distributionChannels?.[0]?.id

	if (!storeChannelId || !variant.availability?.channels) {
		return false
	}

	return (
		Object.values(variant.availability.channels).some(channelAvailability => {
			return (
				channelAvailability.id === storeChannelId &&
				channelAvailability.isOnStock
			)
		}) || false
	)
}

export default OutfitDetailsPage

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const { outfit } = data
	const title = outfit.name ? 'Shop the Look | ' + outfit.name : 'Shop the Look'
	const description = outfit.comment

	const metaTags = [
		{
			title: title,
		},
		{
			property: 'og:title',
			content: title,
		},
		{
			name: 'twitter:title',
			content: title,
		},
		{
			name: 'description',
			content: description,
		},
		{
			property: 'og:description',
			content: description,
		},
		{
			name: 'twitter:description',
			content: description,
		},
	]

	if (outfit.mediaType === 'IMAGE') {
		metaTags.push(
			{
				property: 'og:image',
				content: outfit.mediaUrl,
			},
			{
				name: 'twitter:image',
				content: outfit.mediaUrl,
			},
		)
	} else if (outfit.mediaType === 'VIDEO') {
		metaTags.push(
			{
				property: 'og:video',
				content: outfit.mediaUrl,
			},
			{
				name: 'twitter:player',
				content: outfit.mediaUrl,
			},
		)
	}

	return metaTags
}
